import React, { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Dialog, DialogContent } from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { PREFERENCES_EMAIL } from '~/constants';
import usePreferences from '../../../hooks/use-preferences';
import ToastService from '../../../services/toast-service';
import ProjectService from '../../../services/project-service';
import AsperaOnboardingService from '../../../services/aspera-onboarding-service';

import { DEFAULT_TAB } from '~/modules/hooks/use-preferences-route';
import ContactListPreferences from '~/modules/components/global/preferences/contact-lists';
import ProjectPreferences from '~/modules/components/global/preferences/projects';
import AssetTypePreferences from '~/modules/components/global/preferences/asset-types';
import { captureError } from '../../../pixwel/exception-handler.factory';
import useAuth from '~/modules/hooks/use-auth';
import { useQuery } from '@tanstack/react-query';
import ProfilePreferences from '~/modules/components/global/preferences/profile-preferences';
import ApiTokens from '~/modules/components/global/preferences/api-tokens';
import Webhooks from '~/modules/components/global/preferences/webhooks';
import ServiceAccounts, { manageableGroups } from '~/modules/components/global/preferences/service-accounts';
import { useTerritories } from '~/modules/hooks/use-territories';
import { useAssetTypes } from '~/modules/hooks/use-asset-types';
import { assetTypeLabel } from '~/constants/asset-types';

const EMAIL_OPTIONS = [
	{ label: 'Immediate', value: 0 },
	{ label: 'Hourly', value: 3600 },
	{ label: 'Daily', value: 86400 },
	{ label: 'Weekly', value: 604800 },
	{ label: 'Off', value: -1 }
];

const TAB_LABEL_OVERRIDES = { 'api-tokens': 'API Tokens' };
const buildTabs = (keys) => keys.map((key) => ({
	key,
	label: TAB_LABEL_OVERRIDES[key] ?? key.split('-').map((w) => w[0].toUpperCase() + w.slice(1))
		.join(' ')
}));

function Preferences(props) {
	const {
		show = false,
		onClose,
		tab,
		onTabSelected
	} = props;

	// The selected tab is owned by the URL when the caller supplies it, so a refresh lands back
	// on the same tab. Local state is the fallback for any caller that does not.
	const [localTab, setLocalTab] = useState(DEFAULT_TAB);
	const requestedTab = tab || localTab;
	const setActiveTab = onTabSelected || setLocalTab;
	const isAsperaEnabled = useSyncExternalStore(
		AsperaOnboardingService.subscribe,
		AsperaOnboardingService.isEnabled
	);
	const [isEnablingAspera, setIsEnablingAspera] = useState(false);
	const [preferences, setPreferences] = useState(null);
	const [user, setUser] = useState(null);
	const [blacklistedProjects, setBlacklistedProjects] = useState([]);
	const [projectParams, setProjectParams] = useState({ archived: false, limit: 50, search: '' });

	const { preferences: currentPreferences, isSaving, updatePreferences } = usePreferences();
	const { session, updateUser, isUserSaving } = useAuth();

	// Shown only to people actually named as a manager on at least one group. An admin who is not
	// named sees no tab: they hold the capability implicitly on the API, but there would be no group
	// for them to create in here, and they manage groups from the Admin area anyway.
	const canManageServiceAccounts = useMemo(
		() => manageableGroups(session).length > 0,
		[session]
	);

	// Profile first: it holds the account's own name, address and second factor, which is what
	// people open this for. Notification volume settings are a once-a-year visit.
	const TAB_KEYS = [
		'profile',
		'notifications',
		'ingest',
		'custom-groups',
		'api-tokens',
		'webhooks',
		...(canManageServiceAccounts ? ['service-accounts'] : [])
	];
	const TABS = buildTabs(TAB_KEYS);

	// The URL names the tab, and a URL can name anything: a tab that does not exist, or one this
	// person was deliberately not given. Either opens on Profile rather than on an empty panel --
	// or on content that was meant to be absent.
	const activeTab = TAB_KEYS.includes(requestedTab) ? requestedTab : DEFAULT_TAB;
	const { assetTypes, isLoading: areAssetTypesLoading } = useAssetTypes();

	const { isInitialLoading: areProjectsLoading, data: projects } = useQuery(
		['getProjectsPreferences', projectParams],
		() => ProjectService.getAllProjectsShort(projectParams),
		{ refetchOnWindowFocus: false, enabled: show }
	);

	const { territories } = useTerritories({
		staleTime: 8 * 60 * 60 * 1000,
		cacheTime: 8 * 60 * 60 * 1000
	});

	const territoriesOptions = _.isArray(territories)
		? territories.map((t) => ({ label: t.name, value: t.id }))
		: [];

	const assetTypeOptions = useMemo(() => {
		if(!_.isArray(assetTypes)) {
			return [];
		}

		return assetTypes.map((assetType) => ({
			label: assetTypeLabel(assetType),
			value: assetType.id || assetType.name
		}));
	}, [assetTypes]);

	useEffect(() => {
		if(session) {
			setUser({
				name: session.name,
				emailNormal: session.emailNormal,
				territory: session.territory,
				hasEnforced2fa: session?.hasEnforced2fa
			});
		}
	}, [session]);

	useEffect(() => {
		if(projects?.totalCount > projects?.nodes?.length && !areProjectsLoading) {
			setProjectParams({ ...projectParams, limit: projects.totalCount });
		}
	}, [projects?.totalCount]);

	useEffect(() => {
		if(currentPreferences) {
			setPreferences(currentPreferences);
		}
	}, [currentPreferences]);

	const currentBlacklist = currentPreferences?.blacklist?.projects || [];
	useEffect(() => {
		if(currentBlacklist.length && show && projects?.totalCount && projectParams.search === '') {
			const blacklisted = currentBlacklist.map((id) => projects?.nodes.find((p) => p.value === id) || null);
			setBlacklistedProjects(blacklisted.filter(Boolean));
		}
	}, [currentBlacklist, projects, show]);

	const handlePreferencesChange = (type, field, value) => {
		setPreferences({ ...preferences, [type]: { ...preferences[type], [field]: value } });
	};

	const handleProjectsPreferencesChange = (selected) => {
		handlePreferencesChange('blacklist', 'projects', selected.map((p) => p.value));
		setBlacklistedProjects(selected);
	};

	const blacklistedAssetTypes = useMemo(() => {
		const assetTypeBlacklist = preferences?.blacklist?.assetTypes || [];
		return assetTypeBlacklist
			.map((id) => assetTypeOptions.find((assetType) => assetType.value === id) || null)
			.filter(Boolean);
	}, [assetTypeOptions, preferences?.blacklist?.assetTypes]);

	const handleAssetTypesPreferencesChange = (selected) => {
		handlePreferencesChange('blacklist', 'assetTypes', selected.map((assetType) => assetType.value));
	};

	/**
	 * Whether the profile fields differ from the session we loaded them from.
	 *
	 * One answer for both the button's state and the save itself: those used to disagree, the
	 * button comparing territories by value and the save by reference.
	 */
	const isProfileChanged = () => {
		return !!user && (user.name !== session.name
			|| !_.isEqual(user.territory, session.territory));
	};

	// The model requires a name, so an empty one is refused by the API rather than saved as a
	// blank. The button stays down instead, and the field says why.
	const isProfileValid = () => !!user?.name?.trim();

	const isSaveDisabled = () => {
		if(isSaving || isUserSaving || !isProfileValid()) {
			return true;
		}

		return _.isEqual(currentPreferences, preferences) && !isProfileChanged();
	};

	const handleSave = async() => {
		try {
			if(isProfileChanged()) {
				await updateUser({ ...user, name: user.name.trim() });
			}
			if(!_.isEqual(currentPreferences, preferences)) {
				await updatePreferences(preferences);
			}
			onClose();
			ToastService.create('info', 'User preferences saved');
		}
		catch(err) {
			captureError(err, `Error saving preferences: ${err.message}`);
		}
	};

	const handleCancel = () => {
		setPreferences(currentPreferences);
		setBlacklistedProjects(currentPreferences?.blacklist?.projects || []);
		setUser({
			name: session.name,
			emailNormal: session.emailNormal,
			territory: session.territory,
			hasEnforced2fa: session?.hasEnforced2fa
		});
		onClose();
	};

	const renderNotifications = () => (
		<div className="space-y-6" data-testid="preferences-notifications">
			<div>
				<div className="flex items-center gap-2 mb-4">
					<Checkbox
						id="enableEmailNotifications"
						checked={preferences.mail.enabled}
						onCheckedChange={(checked) => handlePreferencesChange('mail', 'enabled', checked)}
					/>
					<label htmlFor="enableEmailNotifications" className="text-sm font-semibold cursor-pointer">
						Email Notifications
					</label>
				</div>
				<div className="space-y-3">
					{PREFERENCES_EMAIL.map((opt) => (
						<div key={opt.value} className="flex items-center justify-between gap-4" style={{ maxWidth: '24rem' }}>
							<label className="text-sm">{opt.label}</label>
							<Select
								value={String(preferences.mail[opt.value])}
								onValueChange={(val) => handlePreferencesChange('mail', opt.value, Number(val))}
								disabled={!preferences.mail.enabled}
							>
								<SelectTrigger style={{ width: '10rem' }}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{EMAIL_OPTIONS.map((o) => (
										<SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					))}
				</div>
			</div>

			<div>
				<p className="text-sm font-semibold mb-3">Projects with notifications disabled</p>
				<ProjectPreferences
					projects={blacklistedProjects}
					options={projects?.nodes || []}
					setProjects={handleProjectsPreferencesChange}
					isLoading={areProjectsLoading}
				/>
			</div>

			<div className="preferences-notifications-asset-types">
				<p className="text-sm font-semibold mb-3">Asset type notifications disabled</p>
				<AssetTypePreferences
					assetTypes={blacklistedAssetTypes}
					options={assetTypeOptions}
					setAssetTypes={handleAssetTypesPreferencesChange}
					isLoading={areAssetTypesLoading}
				/>
			</div>
		</div>
	);

	// Turning it on asks the browser for the permission there and then, so the
	// toggle settles into its real state rather than promising something the next
	// transfer has to make good on.
	const handleAsperaToggle = async(isChecked) => {
		if( !isChecked ) {
			AsperaOnboardingService.disable();
			return;
		}

		setIsEnablingAspera(true);

		try {
			await AsperaOnboardingService.enable();
		}
		finally {
			setIsEnablingAspera(false);
		}
	};

	const renderIngest = () => (
		<div className="space-y-4" data-testid="preferences-ingest">
			<div className="flex items-center gap-3">
				<Checkbox
					id="skipScratchPreview"
					data-testid="preferences-ingest-skipScratchPreview-input"
					checked={preferences.ingest?.skipScratchPreview || false}
					onCheckedChange={(checked) => handlePreferencesChange('ingest', 'skipScratchPreview', checked)}
				/>
				<label htmlFor="skipScratchPreview" className="text-sm cursor-pointer">
					Disable scratch preview for ingests (speeds up processing)
				</label>
			</div>
			<div className="flex items-center gap-4">
				<label htmlFor="maxConcurrentUploads" className="text-sm">
					Maximum concurrent uploads
				</label>
				<Input
					id="maxConcurrentUploads"
					type="text"
					data-testid="preferences-ingest-maxConcurrentUploads-input"
					value={preferences.ingest?.maxUploads || ''}
					onChange={(e) => handlePreferencesChange('ingest', 'maxUploads', e.target.value)}
					className="w-16 text-center"
				/>
			</div>
			<div className="flex items-center gap-3">
				<Checkbox
					id="ingestModalAutoclose"
					data-testid="preferences-ingest-ingestModalAutoclose-input"
					checked={preferences.ingest?.ingestModalAutoclose || false}
					onCheckedChange={(checked) => handlePreferencesChange('ingest', 'ingestModalAutoclose', checked)}
				/>
				<label htmlFor="ingestModalAutoclose" className="text-sm cursor-pointer">
					Auto-close ingest modal after processing
				</label>
			</div>
			{ /* Held in this browser rather than on the account: it records a
			     decision about this machine's Aspera install, and it is the only
			     way back for someone who turned the offer down. */ }
			<div className="flex items-center gap-3">
				<Checkbox
					id="asperaEnabled"
					data-testid="preferences-ingest-aspera-toggle"
					checked={isAsperaEnabled}
					disabled={isEnablingAspera}
					onCheckedChange={(checked) => handleAsperaToggle(checked)}
				/>
				<label htmlFor="asperaEnabled" className="text-sm cursor-pointer">
					Use Aspera for transfers (this browser)
				</label>
			</div>
		</div>
	);

	if(!preferences) {
		return null;
	}

	return (
		<Dialog open={show} onOpenChange={(open) => {
			if(!open) {
				onClose();
			}
		}}
		>
			<DialogContent
				className="rounded-xl p-0 overflow-hidden flex flex-col"
				style={{ maxWidth: '44rem', height: 'min(90vh, 680px)' }}
				onInteractOutside={(e) => e.preventDefault()}
			>
				<Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
					<div className="px-6 pt-6 shrink-0">
						<h2 className="text-2xl font-medium text-foreground mb-4">Preferences</h2>
						<TabsList variant="line">
							{TABS.map((tab) => (
								<TabsTrigger
									key={tab.key}
									value={tab.key}
									data-testid={`tabs-${tab.key}-radio`}
								>
									{tab.label}
								</TabsTrigger>
							))}
						</TabsList>
					</div>

					<div className="px-6 pb-6 flex-1 overflow-y-auto min-h-0">
						<TabsContent value="notifications">
							{renderNotifications()}
						</TabsContent>
						<TabsContent value="ingest">
							{renderIngest()}
						</TabsContent>
						<TabsContent value="profile">
							<ProfilePreferences
								session={session}
								territoriesOptions={territoriesOptions}
								user={user}
								setUser={setUser}
								handlePreferencesChange={handlePreferencesChange}
								preferences={preferences}
							/>
						</TabsContent>
						<TabsContent value="custom-groups">
							<ContactListPreferences
								currentPreferences={currentPreferences}
								isSaving={isSaving}
								updatePreferences={updatePreferences}
								session={session}
							/>
						</TabsContent>
						<TabsContent value="api-tokens">
							<ApiTokens />
						</TabsContent>
						<TabsContent value="webhooks">
							<Webhooks />
						</TabsContent>
						{canManageServiceAccounts && (
							<TabsContent value="service-accounts">
								<ServiceAccounts />
							</TabsContent>
						)}
					</div>

					<div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
						<Button
							variant="outline"
							onClick={handleCancel}
							disabled={isSaving}
							data-testid="preferences-cancel-btn"
						>
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							disabled={isSaveDisabled()}
							data-testid="preferences-save-btn"
						>
							{isSaving ? 'Saving…' : 'Save Preferences'}
						</Button>
					</div>
				</Tabs>
			</DialogContent>
		</Dialog>
	);
}

Preferences.propTypes = {
	show: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	tab: PropTypes.string,
	onTabSelected: PropTypes.func
};

export default Preferences;
